import { LambdaInvocationTypes, MedicalProfileFiles } from '..';
import * as Dynamo from '../DynamoTypes';

export type FileDetailsImageType = 'pdf' | 'image';

export type Dimensions = {
    width: number;
    height: number;
};

export type PDFFileImageDetails = {
    type: 'pdf-image';
    downloadUrl: string;
    dimensions: Dimensions;
    rotation: MedicalProfileFiles.Rotation;
};

export type ImageFileImageDetails = {
    type: 'image';
    downloadUrl: string;
    dimensions: Dimensions;
    rotation: MedicalProfileFiles.Rotation;
};

export type AudioFileDetails = {
    type: 'audio';
    downloadUrl: string;
    transcript: MedicalProfileFiles.Transcript | null;
};

export type ImageDetails = PDFFileImageDetails | ImageFileImageDetails;

export type CreateMedicalProfileFileRequest = {
    tempFileId: string;
    fileName: string;
    contentType: string;
    medicalProfileFileFolderId: string;
};

export type CreateMedicalProfileFilesRequest = {
    tempFiles: {
        tempFileId: string;
        fileName: string;
        contentType: string;
    }[];
    medicalProfileFileFolderId: string;
};

export type MedicalProfileFileListItem = {
    medicalProfileFileId: string;
    medicalProfileId: string;
    fileName: string;
    processingType: MedicalProfileFiles.FileProcessingType | null;
    contentType: string;
    dateAdded: string;
    uploadUser: Dynamo.User | null;
    summary: string | null;
    pageCount: number | null;
    detailedSummary: string | null;
    processingStatus: MedicalProfileFiles.MedicalProfileFileProcessingStatus;
    medicalProfileFileFolderId: string;
    failureType?: MedicalProfileFiles.MedicalProfileFileFailureType;
    sourceEntityType: Dynamo.MedicalProfileFileSourceEntityType;
    sourceEntityId: string;
    sourceFileId: string | null;
};

export type ListMedicalProfileFilesResponse = {
    medicalProfileFiles: MedicalProfileFileListItem[];
};

export type GetDownloadUrlResponse = {
    downloadUrl: string;
};

export type SearchType = LambdaInvocationTypes.MedicalProfileFileQueryType;

export type MedicalProfileFileSearch = {
    search: string;
    type: SearchType;
};

export type FolderPathPart = {
    folderId: string;
    folderName: string;
};

type WithFullFolderPath<T> = T & {
    fullFolderPath: FolderPathPart[];
};

type WithSearchedFileMetadata<T> = T &
    WithFullFolderPath<{
        fileName: string;
    }>;

export type MedicalProfileFileAudioSearchItem = WithSearchedFileMetadata<LambdaInvocationTypes.AudioBasedSearchItem>;
export type MedicalProfileFileImageSearchItem = WithSearchedFileMetadata<LambdaInvocationTypes.ImageBasedSearchItem>;
export type MedicalProfileFileSearchItem = MedicalProfileFileAudioSearchItem | MedicalProfileFileImageSearchItem;

export type MedicalProfileFileSearchResponse = {
    fileContentsSearchResults: MedicalProfileFileSearchItem[];
    fileNameSearchResults: MedicalProfileFileListItem[];
    folderNameSearchResults: FolderWithDescendantFileCount[];
};

export type GetPageImageResponse = {
    imageDetails: ImageDetails | null;
};

export type GetAudioFileResponse = {
    audioDetails: AudioFileDetails | null;
};

type WithDescendantFilesCount<T> = T & {
    descendantFilesCount: number;
};

type ModifyMedicalProfileFileFolderRequest = {
    parentMedicalProfileFileFolderId: string | 'root';
    folderName: string;
};

export type FolderWithDescendantFileCount = WithDescendantFilesCount<MedicalProfileFileFolder>;

export type CreateMedicalProfileFileFolderRequest = ModifyMedicalProfileFileFolderRequest;
export type CreateMedicalProfileFileFolderResponse = WithFullFolderPath<MedicalProfileFileFolder>;

export type UpdateMedicalProfileFileFolderRequest = Partial<ModifyMedicalProfileFileFolderRequest>;
export type UpdateMedicalProfileFileFolderResponse = WithFullFolderPath<MedicalProfileFileFolder>;

export type GetMedicalProfileFileFolderResponse = WithFullFolderPath<FolderWithDescendantFileCount>;

export type MedicalProfileFileFolder = Dynamo.MedicalProfileFileFolder;

export type GetMedicalProfileFileFolderImmediateDescendantsResponse = {
    medicalProfileFileFolders: FolderWithDescendantFileCount[];
    medicalProfileFiles: MedicalProfileFileListItem[];
};

export type GetMedicalProfileFileResponse = {
    medicalProfileFile: MedicalProfileFileListItem;
};

export type UpdateFileFolderRequest = {
    parentMedicalProfileFileFolderId: string;
};

export type UpdateMedicalProfileFileRequest = {
    fileName: string;
};
